import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { invoiceApi } from '../services/friendlyApi';
import type {
  InvoiceCreate, InvoiceUpdate, InvoiceLineCreate, ListInvoicesParams, InvoiceListFilterParams,
  InvoiceShipmentLinkPayload,
} from '../types';
import { ACCOUNTS_QUERY_KEY } from './useAccounts';
import {
  useDistributeInvoiceApiV1AccountingInvoicesInvoiceIdDistributePost,
} from '../../../generated/api/accounting-invoices/accounting-invoices';
import {
  getListDocumentDistributionsApiV1DocumentDistributionDistributionsGetQueryKey,
} from '../../../generated/api/shared-document-distribution/shared-document-distribution';

export const INVOICES_QUERY_KEY = 'invoices';

export function useInvoices(params?: ListInvoicesParams, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: [INVOICES_QUERY_KEY, params],
    queryFn: () => invoiceApi.list(params),
    placeholderData: keepPreviousData,
    // Opt-in gate: callers that only need invoices under a condition (e.g. a
    // dialog that's mounted but closed) pass enabled:false to skip the fetch.
    enabled: options?.enabled ?? true,
  });
}

/**
 * Aggregate figures over the whole filtered invoice set (ui-list-pages.md §11).
 *
 * Keyed on the filters ALONE, which is the point of the separate request: paging
 * through the list doesn't refetch or invalidate it, because turning a page does
 * not change what the figures describe.
 */
export function useInvoiceAggregates(
  params?: InvoiceListFilterParams,
  options?: { enabled?: boolean },
) {
  return useQuery({
    queryKey: [INVOICES_QUERY_KEY, 'aggregates', params],
    queryFn: () => invoiceApi.aggregates(params),
    placeholderData: keepPreviousData,
    enabled: options?.enabled ?? true,
  });
}

export function useInvoice(id: string) {
  return useQuery({
    queryKey: [INVOICES_QUERY_KEY, id],
    queryFn: () => invoiceApi.get(id),
    enabled: !!id,
  });
}

/**
 * TMS B1/B2 sell-side: the freight shipment(s) this AR invoice bills.
 *
 * The route is forwarder-only and 404s a non-forwarder tenant by design, so no
 * caller should fire it for one. The gate is the `<IfPersona forwarder>` wrapper
 * around the section: it withholds its children until the persona resolves TRUE,
 * so the component never mounts and this query never runs. `enabled` is here for
 * a future caller that needs a second condition, not for the persona.
 */
export function useInvoiceShipments(id: string, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: [INVOICES_QUERY_KEY, id, 'shipments'],
    queryFn: () => invoiceApi.listShipments(id),
    enabled: !!id && (options?.enabled ?? true),
  });
}

/**
 * A link written from HERE is read from over there, so both ends are invalidated.
 *
 * The shipment side renders the same relationship on `ShipmentBillsSection`'s AR-invoice
 * list, in a different module with its own query keys — so invalidating only the
 * invoice keys leaves that panel showing a link this mutation just changed, for as
 * long as it stays mounted. The kinds are named but the shipment ids are not, on
 * purpose: a re-link touches the shipment it moved AWAY from as well, and that id is
 * not in the payload.
 */
function invalidateBothEndsOfTheShipmentLink(queryClient: ReturnType<typeof useQueryClient>) {
  queryClient.invalidateQueries({ queryKey: [INVOICES_QUERY_KEY] });
  queryClient.invalidateQueries({ queryKey: ['shipments', 'invoices'] });
  queryClient.invalidateQueries({ queryKey: ['shipments', 'candidate-invoices'] });
  queryClient.invalidateQueries({ queryKey: ['shipments', 'charges'] });
}

/** Set or clear the single-shipment arm of an invoice's freight link. */
export function useLinkInvoiceShipment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: InvoiceShipmentLinkPayload }) =>
      invoiceApi.linkShipment(id, data),
    onSuccess: () => invalidateBothEndsOfTheShipmentLink(queryClient),
  });
}

/**
 * Release one shipment from this invoice, whichever arm binds them.
 *
 * The invoices key covers both the link list and the invoice itself (clearing the
 * direct arm changes `freight_shipment_id` on the record); the shipments keys cover
 * the far end, which renders the same relationship — including `charges`, since the
 * charge arm flips `invoiced`/`invoice_id` back on that shipment's sell charges.
 */
export function useUnlinkInvoiceShipment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, shipmentId }: { id: string; shipmentId: string }) =>
      invoiceApi.unlinkShipment(id, shipmentId),
    onSuccess: () => invalidateBothEndsOfTheShipmentLink(queryClient),
  });
}

export function useCreateInvoice() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: InvoiceCreate) => invoiceApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [INVOICES_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [ACCOUNTS_QUERY_KEY] });
    },
  });
}

export function useUpdateInvoice() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: InvoiceUpdate }) =>
      invoiceApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [INVOICES_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [ACCOUNTS_QUERY_KEY] });
    },
  });
}

export function useDeleteInvoice() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => invoiceApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [INVOICES_QUERY_KEY] });
    },
  });
}

export function useSendInvoice() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => invoiceApi.send(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [INVOICES_QUERY_KEY] });
    },
  });
}

export function useVoidInvoice() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => invoiceApi.void(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [INVOICES_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [ACCOUNTS_QUERY_KEY] });
    },
  });
}

export function useWriteOffInvoice() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      invoiceApi.writeOff(id, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [INVOICES_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [ACCOUNTS_QUERY_KEY] });
    },
  });
}

export function useCloneInvoice() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => invoiceApi.clone(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [INVOICES_QUERY_KEY] });
    },
  });
}

export function useBulkSendInvoices() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (invoiceIds: string[]) => invoiceApi.bulkSend(invoiceIds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [INVOICES_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [ACCOUNTS_QUERY_KEY] });
    },
  });
}

export function useBulkVoidInvoices() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (invoiceIds: string[]) => invoiceApi.bulkVoid(invoiceIds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [INVOICES_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [ACCOUNTS_QUERY_KEY] });
    },
  });
}

export function useCreateCreditNote() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      lines,
      reason,
    }: {
      id: string;
      lines?: InvoiceLineCreate[];
      reason?: string;
    }) => invoiceApi.createCreditNote(id, { lines, reason }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [INVOICES_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [ACCOUNTS_QUERY_KEY] });
    },
  });
}

/**
 * Distribute (email / re-share) an invoice or credit note to its customer.
 * On success refreshes the document-distribution history so the send log and
 * any open-tracking badges update without a manual reload.
 */
export function useDistributeInvoice() {
  const queryClient = useQueryClient();

  return useDistributeInvoiceApiV1AccountingInvoicesInvoiceIdDistributePost({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: getListDocumentDistributionsApiV1DocumentDistributionDistributionsGetQueryKey(),
        });
      },
    },
  });
}
